import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { MatchSummary, PlatformSettings, PublicUser } from '@bn/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Alert, Badge, Button, Card, Field, Spinner } from '@/components/ui';

interface AdminUser extends PublicUser {
  mustChangePassword: boolean;
  isBot: boolean;
  rating: number | null;
  played: number;
}

type Onglet = 'comptes' | 'reglages' | 'parties';

const dateFormat = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

export function AdminPage() {
  const [onglet, setOnglet] = useState<Onglet>('comptes');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Administration</h1>
        <p className="text-sm text-slate-400">
          Comptes joueurs, réglages de partie et supervision.
        </p>
      </div>

      <nav className="flex gap-1 border-b border-slate-800">
        {(
          [
            ['comptes', 'Comptes'],
            ['reglages', 'Réglages'],
            ['parties', 'Parties'],
          ] as Array<[Onglet, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setOnglet(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              onglet === key
                ? 'border-sky-400 text-sky-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {onglet === 'comptes' && <ComptesPanel />}
      {onglet === 'reglages' && <ReglagesPanel />}
      {onglet === 'parties' && <PartiesPanel />}
    </div>
  );
}

function ComptesPanel() {
  const { user: moi } = useAuth();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ username: string; password: string } | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'player' | 'admin'>('player');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const data = await api.get<{ users: AdminUser[] }>('/api/admin/users');
      setUsers(data.users);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Chargement impossible.');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function run(action: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Opération impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function creer(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const created = await api.post<{
        user: PublicUser;
        temporaryPassword: string | null;
      }>('/api/admin/users', {
        username,
        role,
        ...(password ? { password } : {}),
      });
      setSecret(
        created.temporaryPassword
          ? { username: created.user.displayName, password: created.temporaryPassword }
          : null,
      );
      setUsername('');
      setPassword('');
      setRole('player');
    });
  }

  return (
    <div className="space-y-4">
      {error && <Alert>{error}</Alert>}

      {secret && (
        <Alert type="succes">
          Mot de passe temporaire de <strong>{secret.username}</strong> :{' '}
          <code className="rounded bg-slate-900 px-1.5 py-0.5 font-mono">
            {secret.password}
          </code>{' '}
          — communiquez-le au joueur, il ne sera plus affiché.
        </Alert>
      )}

      <Card title="Créer un compte">
        <form onSubmit={(e) => void creer(e)} className="grid gap-3 md:grid-cols-4 md:items-end">
          <Field
            label="Identifiant"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            hint="Lettres, chiffres, . _ -"
          />
          <Field
            label="Mot de passe"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="Vide = généré automatiquement"
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-300">Rôle</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'player' | 'admin')}
              className="w-full rounded-lg border border-slate-600 bg-slate-950/70 px-3 py-2 text-slate-100"
            >
              <option value="player">Joueur</option>
              <option value="admin">Administrateur</option>
            </select>
          </label>
          <Button type="submit" disabled={busy}>
            Créer le compte
          </Button>
        </form>
      </Card>

      <Card title="Comptes existants">
        {users === null ? (
          <Spinner />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-left text-slate-400">
                  <th className="py-2 pr-3 font-medium">Identifiant</th>
                  <th className="py-2 pr-3 font-medium">Rôle</th>
                  <th className="py-2 pr-3 font-medium">État</th>
                  <th className="py-2 pr-3 text-right font-medium">ELO</th>
                  <th className="py-2 pr-3 text-right font-medium">Parties</th>
                  <th className="py-2 pr-3 font-medium">Dernière activité</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-800/60">
                    <td className="py-2 pr-3 font-medium text-slate-100">
                      {u.displayName}
                      {u.mustChangePassword && (
                        <span className="ml-2 text-xs text-amber-400">mdp temporaire</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge tone={u.isBot ? 'neutre' : u.role === 'admin' ? 'info' : 'neutre'}>
                        {u.isBot ? 'ordinateur' : u.role === 'admin' ? 'admin' : 'joueur'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge tone={u.status === 'active' ? 'succes' : 'alerte'}>
                        {u.status === 'active' ? 'actif' : 'suspendu'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-slate-300">
                      {u.rating ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-400">{u.played}</td>
                    <td className="py-2 pr-3 text-slate-400">
                      {u.lastSeenAt ? dateFormat.format(new Date(u.lastSeenAt)) : 'jamais'}
                    </td>
                    <td className="py-2">
                      {u.isBot ? (
                        <span className="text-xs text-slate-500">
                          compte technique — non modifiable
                        </span>
                      ) : (
                      <div className="flex flex-wrap gap-1.5">
                        <Button
                          variant="secondaire"
                          disabled={busy || u.id === moi?.id}
                          onClick={() =>
                            void run(async () => {
                              await api.patch(`/api/admin/users/${u.id}`, {
                                status: u.status === 'active' ? 'suspended' : 'active',
                              });
                            })
                          }
                        >
                          {u.status === 'active' ? 'Suspendre' : 'Réactiver'}
                        </Button>
                        <Button
                          variant="secondaire"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await api.patch(`/api/admin/users/${u.id}`, {
                                role: u.role === 'admin' ? 'player' : 'admin',
                              });
                            })
                          }
                        >
                          {u.role === 'admin' ? 'Rétrograder' : 'Promouvoir'}
                        </Button>
                        <Button
                          variant="secondaire"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              const res = await api.post<{ temporaryPassword: string }>(
                                `/api/admin/users/${u.id}/reset-password`,
                              );
                              setSecret({
                                username: u.displayName,
                                password: res.temporaryPassword,
                              });
                            })
                          }
                        >
                          Réinitialiser
                        </Button>
                        <Button
                          variant="danger"
                          disabled={busy || u.id === moi?.id}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Supprimer définitivement le compte « ${u.displayName} » et tout son historique ?`,
                              )
                            ) {
                              void run(async () => {
                                await api.delete(`/api/admin/users/${u.id}`);
                              });
                            }
                          }}
                        >
                          Supprimer
                        </Button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ReglagesPanel() {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .get<{ settings: PlatformSettings }>('/api/admin/settings')
      .then((r) => setSettings(r.settings))
      .catch(() => setError('Chargement impossible.'));
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setError(null);
    setDone(false);
    setBusy(true);
    try {
      const saved = await api.put<{ settings: PlatformSettings }>(
        '/api/admin/settings',
        settings,
      );
      setSettings(saved.settings);
      setDone(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!settings) return error ? <Alert>{error}</Alert> : <Spinner />;

  return (
    <Card title="Réglages des parties" className="max-w-xl">
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        <label className="flex items-center gap-3 text-sm text-slate-200">
          <input
            type="checkbox"
            checked={settings.timerEnabled}
            onChange={(e) => setSettings({ ...settings, timerEnabled: e.target.checked })}
            className="size-4 accent-sky-500"
          />
          Activer le chronomètre (le dépassement entraîne la défaite)
        </label>

        <Field
          label="Secondes par coup"
          type="number"
          min={10}
          max={3600}
          disabled={!settings.timerEnabled}
          value={settings.turnSeconds}
          onChange={(e) =>
            setSettings({ ...settings, turnSeconds: Number(e.target.value) })
          }
        />
        <Field
          label="Secondes pour placer sa flotte"
          type="number"
          min={30}
          max={3600}
          disabled={!settings.timerEnabled}
          value={settings.placementSeconds}
          onChange={(e) =>
            setSettings({ ...settings, placementSeconds: Number(e.target.value) })
          }
        />

        <Field
          label="Échecs : secondes par coup"
          type="number"
          min={10}
          max={3600}
          disabled={!settings.timerEnabled}
          value={settings.chessTurnSeconds}
          onChange={(e) =>
            setSettings({ ...settings, chessTurnSeconds: Number(e.target.value) })
          }
        />

        <label className="flex items-center gap-3 text-sm text-slate-200">
          <input
            type="checkbox"
            checked={settings.extraTurnOnHit}
            onChange={(e) =>
              setSettings({ ...settings, extraTurnOnHit: e.target.checked })
            }
            className="size-4 accent-sky-500"
          />
          Un coup au but redonne la main au tireur
        </label>

        <p className="text-xs text-slate-400">
          Ces réglages s’appliquent aux nouvelles parties ; celles déjà en cours conservent
          leur configuration.
        </p>

        {error && <Alert>{error}</Alert>}
        {done && <Alert type="succes">Réglages enregistrés.</Alert>}
        <Button type="submit" disabled={busy}>
          Enregistrer
        </Button>
      </form>
    </Card>
  );
}

function PartiesPanel() {
  const [matches, setMatches] = useState<Array<MatchSummary & { live: boolean }> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const data = await api.get<{ matches: Array<MatchSummary & { live: boolean }> }>(
        '/api/admin/matches',
      );
      setMatches(data.matches);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Chargement impossible.');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!matches) return error ? <Alert>{error}</Alert> : <Spinner />;

  return (
    <Card
      title="Parties"
      actions={
        <Button variant="secondaire" onClick={() => void reload()}>
          Rafraîchir
        </Button>
      }
    >
      {error && <Alert>{error}</Alert>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] text-sm">
          <thead>
            <tr className="border-b border-slate-700 text-left text-slate-400">
              <th className="py-2 pr-3 font-medium">Date</th>
              <th className="py-2 pr-3 font-medium">Joueurs</th>
              <th className="py-2 pr-3 font-medium">État</th>
              <th className="py-2 pr-3 font-medium">Issue</th>
              <th className="py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((match) => (
              <tr key={match.id} className="border-b border-slate-800/60">
                <td className="py-2 pr-3 text-slate-400">
                  {dateFormat.format(new Date(match.createdAt))}
                </td>
                <td className="py-2 pr-3 text-slate-200">
                  {match.players.map((p) => p.displayName).join(' contre ')}
                </td>
                <td className="py-2 pr-3">
                  <Badge tone={match.status === 'active' ? 'info' : 'neutre'}>
                    {match.status === 'active'
                      ? match.live
                        ? 'en cours'
                        : 'en attente'
                      : match.status === 'aborted'
                        ? 'interrompue'
                        : 'terminée'}
                  </Badge>
                </td>
                <td className="py-2 pr-3 text-slate-400">
                  {match.winnerId
                    ? `Victoire de ${
                        match.players.find((p) => p.userId === match.winnerId)?.displayName ??
                        '?'
                      }`
                    : match.status === 'active'
                      ? '—'
                      : 'sans vainqueur'}
                </td>
                <td className="py-2">
                  {match.live && (
                    <Button
                      variant="danger"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm('Interrompre cette partie ?')) {
                          setBusy(true);
                          void api
                            .post(`/api/admin/matches/${match.id}/abort`)
                            .then(reload)
                            .catch((cause: unknown) =>
                              setError(
                                cause instanceof Error ? cause.message : 'Échec.',
                              ),
                            )
                            .finally(() => setBusy(false));
                        }
                      }}
                    >
                      Interrompre
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
